import React, { useRef, useState } from 'react';
import clsx from 'clsx';
import { CompanyLocationsListPreviewOutDTO } from '@api/ringil-api.v2';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { useDetectClickOutside } from '@hooks/useDetectClickOutside/useDetectClickOutside';
import SvgAngleDown from '@icons/angle-down.svg?react';
import { Checkmark } from './Checkmark';
import { isOwnLocation } from '../useCalendarFilter';

interface Props {
  locationsWithRamps: CompanyLocationsListPreviewOutDTO[];
  rampIds: string[];
  /** Receives the new ramp selection; the caller derives the location ids from it. */
  onChange: (rampIds: string[]) => void;
}

/**
 * The calendar's original location → ramp picker, kept selectable through the filter-UI version
 * switch while users get used to the shared tree chips.
 *
 * Unlike the original it holds no state of its own (it used Formik purely to mirror the checkboxes):
 * the ticked boxes are derived from the shared filter, so switching between this and the chip bar
 * shows the same selection either way. Locations are grouped into ours / foreign to match the chips.
 */
export const LegacyLocationRampFilter: React.FC<Props> = ({ locationsWithRamps, rampIds, onChange }) => {
  const { tr } = useTranslationLgs();
  const [opened, setOpened] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useDetectClickOutside([containerRef], () => setOpened(false));

  const rampsOf = (locationId: string) => locationsWithRamps.find(l => l.locationId === locationId)?.ramps.map(r => r.rampId) ?? [];
  const selectedCount = (locationId: string) => rampsOf(locationId).filter(id => rampIds.includes(id)).length;

  const toggleLocation = (locationId: string, checked: boolean) => {
    const locationRamps = rampsOf(locationId);
    const withoutLocation = rampIds.filter(id => !locationRamps.includes(id));
    onChange(checked ? [...withoutLocation, ...locationRamps] : withoutLocation);
  };

  const toggleRamp = (rampId: string, checked: boolean) => {
    onChange(checked ? [...rampIds, rampId] : rampIds.filter(id => id !== rampId));
  };

  const sections = [
    { title: tr('TimeslotReservations.OurLocations', 'Our locations'), locations: locationsWithRamps.filter(isOwnLocation) },
    { title: tr('TimeslotReservations.ForeignLocations', 'Foreign locations'), locations: locationsWithRamps.filter(l => !isOwnLocation(l)) },
  ].filter(s => s.locations.length > 0);

  const renderLocation = (l: CompanyLocationsListPreviewOutDTO) => {
    const locationRamps = rampsOf(l.locationId);
    const allChecked = locationRamps.length > 0 && locationRamps.every(id => rampIds.includes(id));

    return (
      <div key={`wrapper-${l.locationId}`}>
        <label className="items-center px-4 py-3 flex flex-row gap-x-4 space-x-2 cursor-pointer select-none font-bold">
          <input className="peer sr-only" type="checkbox" checked={allChecked} onChange={e => toggleLocation(l.locationId, e.target.checked)} />
          <Checkmark label={l.name} />
        </label>
        <div className="pl-4">
          {l.ramps.map(r => (
            <label key={`location-${l.locationId}-${r.rampId}`} className="items-center flex flex-row gap-x-4 px-4 py-3 space-x-2 cursor-pointer select-none">
              <input className="peer sr-only" type="checkbox" checked={rampIds.includes(r.rampId)} onChange={e => toggleRamp(r.rampId, e.target.checked)} />
              <Checkmark label={r.name} />
            </label>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="relative grow" ref={containerRef}>
      <div
        onClick={() => setOpened(!opened)}
        className={clsx(
          'flex flex-row justify-between rounded-lg cursor-pointer border border-solid px-4 py-3 bg-white leading-[1.125rem] hover:border-content-brand',
          !opened ? 'border-content-brandMedium' : 'border-content-brand',
        )}
      >
        <div className="text-content-gr4 flex flex-wrap gap-2">
          {locationsWithRamps.map((l, index) => (
            <React.Fragment key={l.locationId}>
              <span>
                {l.name} <span className="text-content-primary">({selectedCount(l.locationId)})</span>
              </span>
              {index < locationsWithRamps.length - 1 && <span className="text-content-tertiary">|</span>}
            </React.Fragment>
          ))}
        </div>
        <SvgAngleDown className={clsx('transition-all', opened ? 'rotate-180' : 'rotate-0')} />
      </div>

      <div
        className={clsx(
          'absolute z-50 left-0 top-full translate-y-1.5 w-[26.1875rem] pt-4 pr-4 pb-6 pl-2 shadow-card2 bg-white rounded-lg overflow-y-auto max-h-[calc(100vh-10rem)]',
          opened ? 'block' : 'hidden',
        )}
      >
        {sections.map(section => (
          <div key={section.title}>
            {/* The heading only earns its place when both kinds of location are present. */}
            {sections.length > 1 && <div className="px-4 pt-3 pb-1 text-xs uppercase tracking-wide text-content-secondary">{section.title}</div>}
            {section.locations.map(renderLocation)}
          </div>
        ))}
        <button onClick={() => setOpened(false)} className="text-content-brand transition-all duration-200 hover:text-complementary-brandDark p-0 cursor-pointer mt-6 ml-4 border-0 bg-transparent">
          {tr('TimeslotReservations.Close', 'Close')}
        </button>
      </div>
    </div>
  );
};